/**
 * Maison Grès — operations dashboard (overview, orders, calendar)
 */
(function () {
  const STORAGE_KEY = 'maisonChannelAdminToken';
  const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  const PAGE_TITLES = {
    overview: 'Overview',
    orders: 'Orders',
    calendar: 'Calendar',
    channels: 'Channels'
  };
  const PLATFORM_LABEL = {
    site: 'Site',
    airbnb: 'Airbnb',
    booking: 'Booking.com'
  };

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function toYMD(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  function parseYMD(s) {
    const [y, m, d] = String(s || '').split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }

  function addDays(date, days) {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  }

  function monthLabel(year, monthIndex) {
    return new Date(year, monthIndex, 1).toLocaleString(undefined, {
      month: 'long',
      year: 'numeric'
    });
  }

  function formatDisplay(ymd) {
    const d = parseYMD(ymd);
    if (!d) return '—';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function formatMoney(amount, currency) {
    if (amount == null || amount === '') return '—';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: 0
      }).format(Number(amount));
    } catch {
      return `${Number(amount).toFixed(0)} ${currency || ''}`.trim();
    }
  }

  function formatDateTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function nightsFromStay(checkIn, checkOut) {
    const set = new Set();
    let cursor = parseYMD(checkIn);
    const end = parseYMD(checkOut);
    if (!cursor || !end) return set;
    while (cursor < end) {
      set.add(toYMD(cursor));
      cursor = addDays(cursor, 1);
    }
    return set;
  }

  function nightsInclusive(start, end) {
    let n = 0;
    let cursor = parseYMD(start);
    const last = parseYMD(end);
    if (!cursor || !last || last < cursor) return 0;
    while (cursor <= last) {
      n += 1;
      cursor = addDays(cursor, 1);
    }
    return n;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  class ChannelAdmin {
    constructor(root) {
      this.root = root;
      this.api = (root.dataset.apiUrl || '').replace(/\/$/, '');
      this.token = sessionStorage.getItem(STORAGE_KEY) || '';
      this.units = [];
      this.orders = [];
      this.stats = null;
      this.upcoming = [];
      this.inHouse = [];
      this.sources = {};
      this.feeds = [];
      this.viewName = 'overview';
      this.ordersFilter = 'all';
      this.ordersQuery = '';
      this.ordersPage = 1;
      this.ordersPerPage = 20;
      this.activeHandle = '';
      this.view = new Date();
      this.view.setDate(1);
      this.selectStart = null;
      this.selectEnd = null;
      this.hoverEnd = null;
      this.busy = false;
      this._modalResolver = null;

      this.loginEl = root.querySelector('[data-channel-login]');
      this.appEl = root.querySelector('[data-channel-app]');
      this.loginForm = root.querySelector('[data-channel-login-form]');
      this.loginError = root.querySelector('[data-channel-login-error]');
      this.unitsEl = root.querySelector('[data-channel-units]');
      this.gridEl = root.querySelector('[data-cal-grid]');
      this.bookingsEl = root.querySelector('[data-channel-bookings]');
      this.statusEl = root.querySelector('[data-channel-status]');
      this.toastEl = root.querySelector('[data-channel-toast]');
      this._toastTimer = null;
      this.actionBar = root.querySelector('[data-channel-actionbar]');
      this.selStartEl = root.querySelector('[data-sel-start]');
      this.selEndEl = root.querySelector('[data-sel-end]');
      this.selNightsEl = root.querySelector('[data-sel-nights]');
      this.pageTitle = root.querySelector('[data-page-title]');
      this.modalEl = root.querySelector('[data-channel-modal]');
      this.modalTitle = root.querySelector('[data-modal-title]');
      this.modalBody = root.querySelector('[data-modal-body]');
      this.modalConfirmBtn = root.querySelector('[data-modal-confirm]');

      this.bind();
      if (this.token) this.enterApp();
      else this.showLogin();
    }

    bind() {
      this.loginForm?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.login();
      });
      this.root.querySelector('[data-channel-logout]')?.addEventListener('click', () => this.logout());
      this.root.querySelector('[data-channel-refresh]')?.addEventListener('click', () => {
        if (this.viewName === 'channels') this.syncChannels();
        else this.loadDashboard();
      });
      this.root.querySelector('[data-cal-prev]')?.addEventListener('click', () => {
        this.view.setMonth(this.view.getMonth() - 1);
        this.renderCalendar();
      });
      this.root.querySelector('[data-cal-next]')?.addEventListener('click', () => {
        this.view.setMonth(this.view.getMonth() + 1);
        this.renderCalendar();
      });
      this.root.querySelector('[data-sel-clear]')?.addEventListener('click', () => this.clearSelection());
      this.root.querySelector('[data-sel-block]')?.addEventListener('click', () => this.applySelection('block'));
      this.root.querySelector('[data-sel-unblock]')?.addEventListener('click', () => this.applySelection('unblock'));

      this.root.querySelectorAll('[data-nav]').forEach((btn) => {
        btn.addEventListener('click', () => this.setView(btn.dataset.nav));
      });
      this.root.querySelectorAll('[data-goto]').forEach((btn) => {
        btn.addEventListener('click', () => this.setView(btn.dataset.goto));
      });
      this.root.querySelectorAll('[data-orders-filter]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.ordersFilter = btn.dataset.ordersFilter;
          this.ordersPage = 1;
          this.root.querySelectorAll('[data-orders-filter]').forEach((b) => {
            b.classList.toggle('is-active', b === btn);
          });
          this.renderOrders();
        });
      });
      this.root.querySelector('[data-orders-search]')?.addEventListener('input', (e) => {
        this.ordersQuery = String(e.target.value || '').trim().toLowerCase();
        this.ordersPage = 1;
        this.renderOrders();
      });
      this.root.querySelector('[data-orders-prev]')?.addEventListener('click', () => {
        if (this.ordersPage > 1) {
          this.ordersPage -= 1;
          this.renderOrders();
          this.root.querySelector('[data-orders-list]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
      this.root.querySelector('[data-orders-next]')?.addEventListener('click', () => {
        this.ordersPage += 1;
        this.renderOrders();
        this.root.querySelector('[data-orders-list]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      this.root.querySelectorAll('[data-modal-cancel]').forEach((el) => {
        el.addEventListener('click', () => this.closeModal(false));
      });
      this.modalConfirmBtn?.addEventListener('click', () => this.closeModal(true));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modalEl && !this.modalEl.hidden) this.closeModal(false);
      });
    }

    setView(name) {
      if (!PAGE_TITLES[name]) return;
      this.viewName = name;
      if (this.pageTitle) this.pageTitle.textContent = PAGE_TITLES[name];
      this.root.querySelectorAll('[data-nav]').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.nav === name);
      });
      this.root.querySelectorAll('[data-view]').forEach((el) => {
        el.hidden = el.dataset.view !== name;
      });
      if (name === 'calendar') {
        this.renderUnits();
        this.renderCalendar();
        this.renderBookings();
      }
      if (name === 'orders') this.renderOrders();
      if (name === 'overview') this.renderOverview();
      if (name === 'channels') this.renderChannels();
    }

    openModal({ title, body, confirmLabel = 'Confirm', danger = true }) {
      return new Promise((resolve) => {
        this._modalResolver = resolve;
        if (this.modalTitle) this.modalTitle.textContent = title || 'Confirm';
        if (this.modalBody) this.modalBody.textContent = body || '';
        if (this.modalConfirmBtn) {
          this.modalConfirmBtn.textContent = confirmLabel;
          this.modalConfirmBtn.classList.toggle('maison-channel-admin__btn--danger', danger);
          this.modalConfirmBtn.classList.toggle('maison-channel-admin__btn--primary', !danger);
        }
        if (this.modalEl) this.modalEl.hidden = false;
      });
    }

    closeModal(result) {
      if (this.modalEl) this.modalEl.hidden = true;
      const resolve = this._modalResolver;
      this._modalResolver = null;
      if (resolve) resolve(Boolean(result));
    }

    showLogin() {
      this.loginEl.hidden = false;
      this.appEl.hidden = true;
    }

    async apiFetch(path, options = {}) {
      const headers = Object.assign({ 'Content-Type': 'application/json' }, options.headers || {});
      if (this.token) headers.Authorization = `Bearer ${this.token}`;
      const res = await fetch(`${this.api}${path}`, { ...options, headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      return data;
    }

    async login() {
      this.setLoginError('');
      const fd = new FormData(this.loginForm);
      try {
        const data = await this.apiFetch('/admin/login', {
          method: 'POST',
          body: JSON.stringify({
            username: fd.get('username'),
            password: fd.get('password')
          })
        });
        this.token = data.token;
        sessionStorage.setItem(STORAGE_KEY, this.token);
        await this.enterApp();
      } catch (err) {
        this.setLoginError(err.message || 'Login failed');
      }
    }

    logout() {
      this.token = '';
      sessionStorage.removeItem(STORAGE_KEY);
      this.showLogin();
    }

    setLoginError(msg) {
      if (!this.loginError) return;
      this.loginError.hidden = !msg;
      this.loginError.textContent = msg || '';
    }

    setStatus(msg) {
      if (!this.statusEl) return;
      this.statusEl.hidden = !msg;
      this.statusEl.textContent = msg || '';
    }

    showToast(msg, { error = false } = {}) {
      if (!this.toastEl || !msg) return;
      this.toastEl.hidden = false;
      this.toastEl.textContent = msg;
      this.toastEl.classList.toggle('is-error', Boolean(error));
      this.toastEl.classList.add('is-visible');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        this.toastEl.classList.remove('is-visible');
        this.toastEl.hidden = true;
      }, 3200);
    }

    async enterApp() {
      this.loginEl.hidden = true;
      this.appEl.hidden = false;
      await this.loadDashboard();
      this.setView('overview');
    }

    async loadDashboard() {
      this.setStatus('Loading…');
      try {
        const data = await this.apiFetch('/admin/dashboard');
        this.applyDashboard(data);
        this.setStatus('');
      } catch (err) {
        this.setStatus(err.message);
        if (/unauthorized/i.test(err.message)) this.logout();
      }
    }

    async syncChannels() {
      this.setStatus('Syncing Airbnb / Booking.com…');
      try {
        const data = await this.apiFetch('/admin/sync-channels', { method: 'POST', body: '{}' });
        this.applyDashboard(data);
        const errors = (data.synced || [])
          .flatMap((row) => {
            const msgs = [];
            if (row.airbnb && row.airbnb.ok === false) msgs.push(`${row.handle} Airbnb: ${row.airbnb.error}`);
            if (row.booking && row.booking.ok === false) msgs.push(`${row.handle} Booking: ${row.booking.error}`);
            if (row.error) msgs.push(`${row.handle}: ${row.error}`);
            return msgs;
          });
        if (errors.length) {
          this.setStatus(errors.join(' · '));
          this.showToast(errors.join(' · '), { error: true });
        } else {
          this.setStatus('Channels synced');
          this.showToast('Channels synced');
        }
      } catch (err) {
        this.setStatus(err.message);
        this.showToast(err.message, { error: true });
      }
    }

    applyDashboard(data) {
      this.units = (data.units || []).map((u) => this.normalizeUnit(u));
      this.orders = data.orders || [];
      this.stats = data.stats || null;
      this.upcoming = data.upcoming || [];
      this.inHouse = data.inHouse || [];
      this.sources = data.sources || {};
      this.feeds = data.feeds || this.units.map((u) => ({
        handle: u.handle,
        title: u.title,
        airbnbIcalUrl: u.airbnbIcalUrl || '',
        bookingIcalUrl: u.bookingIcalUrl || '',
        exportPath: u.exportPath || null
      }));
      if (!this.activeHandle && this.units[0]) this.activeHandle = this.units[0].handle;
      this.renderOverview();
      this.renderOrders();
      this.renderChannels();
      if (this.viewName === 'calendar') {
        this.renderUnits();
        this.renderCalendar();
        this.renderBookings();
      }
    }

    normalizeUnit(unit) {
      return Object.assign({}, unit, {
        bookings: Array.isArray(unit.bookings) ? unit.bookings : [],
        airbnb: Array.isArray(unit.airbnb) ? unit.airbnb : [],
        booking: Array.isArray(unit.booking) ? unit.booking : []
      });
    }

    activeUnit() {
      return this.units.find((u) => u.handle === this.activeHandle) || null;
    }

    renderOverview() {
      const s = this.stats || {};
      const statsEl = this.root.querySelector('[data-overview-stats]');
      if (statsEl) {
        const cards = [
          { value: String(s.orders30d ?? 0), label: 'Orders (30d)', note: 'All Shopify' },
          {
            value: formatMoney(s.revenue30d, s.currency),
            label: 'Revenue (30d)',
            note: s.currency || ''
          },
          {
            value: `${s.occupiedTonight ?? 0}/${s.unitsTotal ?? 6}`,
            label: 'Blocked tonight',
            note: `${s.occupancyTonightPct ?? 0}% locked`
          },
          { value: String(s.upcomingStays ?? 0), label: 'Upcoming stays', note: 'Guest check-ins ahead' },
          {
            value: String(s.nightsBookedNext30 ?? 0),
            label: 'Nights locked (30d)',
            note: `of ${s.nightsCapacityNext30 ?? 180} capacity`
          }
        ];

        statsEl.innerHTML = cards
          .map(
            (c) => `
          <article class="maison-channel-admin__stat">
            <p class="maison-channel-admin__stat-value">${escapeHtml(c.value)}</p>
            <p class="maison-channel-admin__stat-label">${escapeHtml(c.label)}</p>
            <p class="maison-channel-admin__stat-note">${escapeHtml(c.note || '')}</p>
          </article>`
          )
          .join('');
      }

      const noteEl = this.root.querySelector('[data-overview-sessions-note]');
      if (noteEl) {
        noteEl.textContent =
          'Tag Shopify orders with “airbnb” or “booking” to sort OTA stays here. Site bookings sync automatically.';
      }

      const platformsEl = this.root.querySelector('[data-overview-platforms]');
      if (platformsEl) {
        const bp = s.byPlatform || { site: 0, airbnb: 0, booking: 0 };
        const total = Math.max(1, (bp.site || 0) + (bp.airbnb || 0) + (bp.booking || 0));
        platformsEl.innerHTML = ['site', 'airbnb', 'booking']
          .map((key) => {
            const n = bp[key] || 0;
            const pct = Math.round((n / total) * 100);
            return `
              <div class="maison-channel-admin__platform-row">
                <div class="maison-channel-admin__platform-meta">
                  <span class="maison-channel-admin__tag maison-channel-admin__tag--${key}">${PLATFORM_LABEL[key]}</span>
                  <strong>${n}</strong>
                </div>
                <div class="maison-channel-admin__bar">
                  <i style="width:${pct}%" class="maison-channel-admin__bar-fill maison-channel-admin__bar-fill--${key}"></i>
                </div>
              </div>`;
          })
          .join('');
      }

      const timelineEl = this.root.querySelector('[data-overview-timeline]');
      if (timelineEl) {
        const items = [
          ...this.inHouse.map((o) => ({ ...o, phase: 'In house' })),
          ...this.upcoming.map((o) => ({ ...o, phase: 'Upcoming' }))
        ].slice(0, 10);

        if (!items.length) {
          timelineEl.innerHTML = `<li class="maison-channel-admin__booking-empty">No stays on the books yet.</li>`;
        } else {
          timelineEl.innerHTML = items
            .map(
              (o) => `
            <li class="maison-channel-admin__timeline-item">
              <span class="maison-channel-admin__tag maison-channel-admin__tag--${o.platform || o.source || 'site'}">${PLATFORM_LABEL[o.platform || o.source] || 'Site'}</span>
              <div>
                <strong>${escapeHtml(o.orderName)}</strong>
                <span>${escapeHtml(o.unitTitle || o.title || '')} · ${escapeHtml(o.customerName || 'Guest')}</span>
                <span>${formatDisplay(o.checkIn)} → ${formatDisplay(o.checkOut)} · ${escapeHtml(o.phase)}</span>
              </div>
            </li>`
            )
            .join('');
        }
      }

      const unitsEl = this.root.querySelector('[data-overview-units]');
      if (unitsEl) {
        unitsEl.innerHTML = this.units
          .map((u) => {
            const blocked = Boolean(u.blockedTonight ?? u.occupiedTonight);
            const state = blocked ? 'Blocked tonight' : 'Available';
            const next7 = Number(u.blockedNext7 ?? 0);
            const monthN = Number(u.blockedThisMonth ?? 0);
            const weekLabel =
              next7 === 0 ? '0 days locked · next 7 days' : `${next7} day${next7 === 1 ? '' : 's'} locked · next 7 days`;
            const monthLabel =
              monthN === 0
                ? '0 days locked · rest of month'
                : `${monthN} day${monthN === 1 ? '' : 's'} locked · rest of month`;
            const guests = Number(u.upcomingStayCount ?? 0);
            const guestLabel =
              guests === 0 ? 'No upcoming guests' : guests === 1 ? '1 upcoming guest' : `${guests} upcoming guests`;
            return `
              <button type="button" class="maison-channel-admin__unit-tile ${blocked ? 'is-busy' : ''}" data-jump-unit="${escapeHtml(u.handle)}">
                <strong>${escapeHtml(u.title)}</strong>
                <span>${state}</span>
                <em>${escapeHtml(weekLabel)}</em>
                <em>${escapeHtml(monthLabel)}</em>
                <em class="maison-channel-admin__unit-tile-guests">${escapeHtml(guestLabel)}</em>
              </button>`;
          })
          .join('');
        unitsEl.querySelectorAll('[data-jump-unit]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.activeHandle = btn.dataset.jumpUnit;
            this.setView('calendar');
          });
        });
      }
    }

    renderOrders() {
      const list = this.root.querySelector('[data-orders-list]');
      const pager = this.root.querySelector('[data-orders-pager]');
      const pageLabel = this.root.querySelector('[data-orders-page-label]');
      const prevBtn = this.root.querySelector('[data-orders-prev]');
      const nextBtn = this.root.querySelector('[data-orders-next]');
      if (!list) return;

      let rows = this.orders.slice();
      if (this.ordersFilter !== 'all') {
        rows = rows.filter((o) => (o.platform || o.source) === this.ordersFilter);
      }
      if (this.ordersQuery) {
        const q = this.ordersQuery;
        rows = rows.filter((o) => {
          const blob = [
            o.orderName,
            o.customerName,
            o.customerEmail,
            o.customerPhone,
            o.unitTitle,
            o.title,
            o.handle,
            o.platform,
            o.note,
            o.tags
          ]
            .join(' ')
            .toLowerCase();
          return blob.includes(q);
        });
      }

      const perPage = this.ordersPerPage || 20;
      const total = rows.length;
      const totalPages = Math.max(1, Math.ceil(total / perPage));
      if (this.ordersPage > totalPages) this.ordersPage = totalPages;
      if (this.ordersPage < 1) this.ordersPage = 1;
      const start = (this.ordersPage - 1) * perPage;
      const pageRows = rows.slice(start, start + perPage);
      const shownFrom = total ? start + 1 : 0;
      const shownTo = Math.min(start + perPage, total);

      if (pager) pager.hidden = total === 0;
      if (pageLabel) {
        pageLabel.textContent = total
          ? `Showing ${shownFrom}–${shownTo} of ${total} · Page ${this.ordersPage} of ${totalPages}`
          : '';
      }
      if (prevBtn) prevBtn.disabled = this.ordersPage <= 1;
      if (nextBtn) nextBtn.disabled = this.ordersPage >= totalPages || total === 0;

      if (!rows.length) {
        list.innerHTML = `<p class="maison-channel-admin__booking-empty">No orders match this filter. Airbnb & Booking.com appear when those stays are tagged or synced.</p>`;
        return;
      }

      list.innerHTML = pageRows
        .map((o) => {
          const platform = o.platform || o.source || 'site';
          const stay = o.hasStay
            ? `${formatDisplay(o.checkIn)} → ${formatDisplay(o.checkOut)}${o.nights ? ` · ${o.nights} nights` : ''}`
            : 'No stay dates';
          const contact = [
            o.customerPhone
              ? `<a href="tel:${escapeHtml(o.customerPhone)}">${escapeHtml(o.customerPhone)}</a>`
              : '',
            o.customerEmail
              ? `<a href="mailto:${escapeHtml(o.customerEmail)}">${escapeHtml(o.customerEmail)}</a>`
              : ''
          ]
            .filter(Boolean)
            .join(' · ');

          const badges = [];
          if (o.cancelled) badges.push('<span class="maison-channel-admin__pill is-warn">Cancelled</span>');
          if (o.datesReleased) badges.push('<span class="maison-channel-admin__pill">Dates released</span>');
          if (o.financialStatus)
            badges.push(`<span class="maison-channel-admin__pill">${escapeHtml(o.financialStatus)}</span>`);

          return `
            <article class="maison-channel-admin__order-card ${o.cancelled ? 'is-cancelled' : ''}">
              <header class="maison-channel-admin__order-head">
                <div>
                  <span class="maison-channel-admin__tag maison-channel-admin__tag--${platform}">${PLATFORM_LABEL[platform] || platform}</span>
                  <strong>${escapeHtml(o.orderName)}</strong>
                  ${badges.join('')}
                </div>
                <div class="maison-channel-admin__order-money">${escapeHtml(formatMoney(o.total?.amount, o.total?.currencyCode))}</div>
              </header>
              <div class="maison-channel-admin__order-grid">
                <div>
                  <span class="maison-channel-admin__micro">Guest</span>
                  <p>${escapeHtml(o.customerName || 'Guest')}</p>
                  <p class="maison-channel-admin__muted">${contact || 'No contact'}</p>
                </div>
                <div>
                  <span class="maison-channel-admin__micro">Unit</span>
                  <p>${escapeHtml(o.unitTitle || o.title || '—')}</p>
                  <p class="maison-channel-admin__muted">${escapeHtml(stay)}</p>
                </div>
                <div>
                  <span class="maison-channel-admin__micro">Placed</span>
                  <p>${escapeHtml(formatDateTime(o.createdAt))}</p>
                  <p class="maison-channel-admin__muted">${escapeHtml(o.sourceName || 'Shopify')}</p>
                </div>
                <div>
                  <span class="maison-channel-admin__micro">Fulfillment</span>
                  <p>${escapeHtml(o.fulfillmentStatus || '—')}</p>
                  <p class="maison-channel-admin__muted">${escapeHtml(o.address || '')}</p>
                </div>
              </div>
              ${o.note ? `<p class="maison-channel-admin__order-note">${escapeHtml(o.note)}</p>` : ''}
              <footer class="maison-channel-admin__order-foot">
                ${
                  o.canRelease
                    ? `<button type="button" class="maison-channel-admin__btn maison-channel-admin__btn--release" data-release-order="${escapeHtml(o.orderName)}" data-release-handle="${escapeHtml(o.handle)}" data-release-in="${escapeHtml(o.checkIn)}" data-release-out="${escapeHtml(o.checkOut)}" data-release-guest="${escapeHtml(o.customerName || '')}">Release dates</button>`
                    : ''
                }
                ${
                  o.handle
                    ? `<button type="button" class="maison-channel-admin__btn maison-channel-admin__btn--ghost" data-jump-cal="${escapeHtml(o.handle)}">View calendar</button>`
                    : ''
                }
              </footer>
            </article>`;
        })
        .join('');

      list.querySelectorAll('[data-release-order]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.releaseBooking({
            title: btn.dataset.releaseOrder,
            orderName: btn.dataset.releaseOrder,
            start: btn.dataset.releaseIn,
            end: btn.dataset.releaseOut,
            customerName: btn.dataset.releaseGuest || '',
            handle: btn.dataset.releaseHandle
          });
        });
      });
      list.querySelectorAll('[data-jump-cal]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.activeHandle = btn.dataset.jumpCal;
          this.setView('calendar');
        });
      });
    }

    renderChannels() {
      const el = this.root.querySelector('[data-channels-grid]');
      if (!el) return;
      const apiBase = this.api;
      const feeds = this.feeds || [];
      const airbnbCount = this.sources.airbnbConnectedCount || feeds.filter((f) => f.airbnbIcalUrl).length;
      const bookingCount = this.sources.bookingConnectedCount || feeds.filter((f) => f.bookingIcalUrl).length;

      el.innerHTML = `
        <p class="maison-channel-admin__channel-status">
          Connected: Airbnb ${airbnbCount}/6 · Booking.com ${bookingCount}/6.
          Refresh syncs OTA calendars into this dashboard and the website.
        </p>
        <div class="maison-channel-admin__feed-list">
          ${feeds
            .map((f) => {
              const exportUrl = f.exportPath ? `${apiBase}${f.exportPath}` : '';
              return `
            <article class="maison-channel-admin__card maison-channel-admin__feed-card" data-feed-handle="${escapeHtml(f.handle)}">
              <h3>${escapeHtml(f.title)}</h3>
              <label class="maison-channel-admin__field">
                <span>Airbnb export URL (paste from Airbnb)</span>
                <input type="url" data-feed-airbnb value="${escapeHtml(f.airbnbIcalUrl || '')}" placeholder="https://www.airbnb.com/calendar/ical/….ics?s=…">
              </label>
              <label class="maison-channel-admin__field">
                <span>Booking.com export URL (optional)</span>
                <input type="url" data-feed-booking value="${escapeHtml(f.bookingIcalUrl || '')}" placeholder="https://admin.booking.com/…">
              </label>
              ${
                exportUrl
                  ? `<label class="maison-channel-admin__field">
                      <span>Import into Airbnb / Booking.com (copy this)</span>
                      <div class="maison-channel-admin__copy-row">
                        <input type="text" readonly value="${escapeHtml(exportUrl)}" data-export-url>
                        <button type="button" class="maison-channel-admin__btn maison-channel-admin__btn--ghost" data-copy-export>Copy</button>
                      </div>
                    </label>`
                  : ''
              }
              <button type="button" class="maison-channel-admin__btn maison-channel-admin__btn--primary" data-save-feed>
                Save & sync
              </button>
            </article>`;
            })
            .join('')}
        </div>`;

      el.querySelectorAll('[data-copy-export]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const input = btn.closest('.maison-channel-admin__copy-row')?.querySelector('[data-export-url]');
          if (!input) return;
          try {
            await navigator.clipboard.writeText(input.value);
            btn.textContent = 'Copied';
            this.showToast('Link copied');
            setTimeout(() => {
              btn.textContent = 'Copy';
            }, 1200);
          } catch {
            input.select();
          }
        });
      });

      el.querySelectorAll('[data-save-feed]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const card = btn.closest('[data-feed-handle]');
          if (!card) return;
          const handle = card.dataset.feedHandle;
          const title = card.querySelector('h3')?.textContent?.trim() || handle;
          const airbnbIcalUrl = card.querySelector('[data-feed-airbnb]')?.value?.trim() || '';
          const bookingIcalUrl = card.querySelector('[data-feed-booking]')?.value?.trim() || '';
          btn.disabled = true;
          this.setStatus(`Saving ${title}…`);
          try {
            await this.apiFetch('/admin/channel-feeds', {
              method: 'POST',
              body: JSON.stringify({ handle, airbnbIcalUrl, bookingIcalUrl })
            });
            await this.loadDashboard();
            this.setStatus(`Saved ${title}`);
            this.showToast(`${title} saved & synced`);
          } catch (err) {
            this.setStatus(err.message);
            this.showToast(err.message, { error: true });
          } finally {
            btn.disabled = false;
          }
        });
      });
    }

    dayMetaMap() {
      const unit = this.activeUnit();
      const map = new Map();
      if (!unit) return map;

      (unit.airbnb || []).forEach((b) => {
        nightsFromStay(b.checkIn, b.checkOut).forEach((n) => {
          map.set(n, { source: 'airbnb', label: b.label || b.orderName || 'Airbnb booking', locked: true });
        });
      });
      (unit.booking || []).forEach((b) => {
        nightsFromStay(b.checkIn, b.checkOut).forEach((n) => {
          map.set(n, { source: 'booking', label: b.label || b.orderName || 'Booking.com', locked: true });
        });
      });
      (unit.bookings || []).forEach((b) => {
        nightsFromStay(b.checkIn, b.checkOut).forEach((n) => {
          const guest = b.customerName ? ` · ${b.customerName}` : '';
          map.set(n, {
            source: 'site',
            label: `${b.orderName || 'Site'}${guest} · ${b.checkIn} → ${b.checkOut}`,
            locked: true
          });
        });
      });
      (unit.nights || []).forEach((n) => {
        if (!map.has(n)) map.set(n, { source: 'blocked', label: 'Blocked manually', locked: false });
      });
      return map;
    }

    renderUnits() {
      if (!this.unitsEl) return;
      this.unitsEl.innerHTML = '';
      this.units.forEach((unit) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'maison-channel-admin__unit' + (unit.handle === this.activeHandle ? ' is-active' : '');
        btn.textContent = unit.title;
        btn.addEventListener('click', () => {
          this.activeHandle = unit.handle;
          this.clearSelection(false);
          this.renderUnits();
          this.renderCalendar();
          this.renderBookings();
        });
        this.unitsEl.appendChild(btn);
      });
    }

    renderBookings() {
      if (!this.bookingsEl) return;
      const unit = this.activeUnit();
      this.bookingsEl.innerHTML = '';
      const items = [];
      (unit?.bookings || []).forEach((b) =>
        items.push({
          source: 'site',
          title: b.orderName,
          start: b.checkIn,
          end: b.checkOut,
          canRelease: true,
          orderName: b.orderName,
          handle: unit.handle,
          customerName: b.customerName || '',
          customerEmail: b.customerEmail || '',
          customerPhone: b.customerPhone || ''
        })
      );
      (unit?.airbnb || []).forEach((b) =>
        items.push({
          source: 'airbnb',
          title: b.orderName || b.label || 'Airbnb',
          start: b.checkIn,
          end: b.checkOut,
          canRelease: Boolean(b.canRelease),
          orderName: b.orderName,
          handle: unit.handle,
          customerName: b.customerName || '',
          customerEmail: b.customerEmail || '',
          customerPhone: b.customerPhone || ''
        })
      );
      (unit?.booking || []).forEach((b) =>
        items.push({
          source: 'booking',
          title: b.orderName || b.label || 'Booking.com',
          start: b.checkIn,
          end: b.checkOut,
          canRelease: Boolean(b.canRelease),
          orderName: b.orderName,
          handle: unit.handle,
          customerName: b.customerName || '',
          customerEmail: b.customerEmail || '',
          customerPhone: b.customerPhone || ''
        })
      );
      items.sort((a, b) => String(a.start).localeCompare(String(b.start)));
      const todayYmd = toYMD(new Date());
      const upcoming = items.filter((item) => !item.end || item.end > todayYmd);

      if (!upcoming.length) {
        const li = document.createElement('li');
        li.className = 'maison-channel-admin__booking-empty';
        li.textContent = 'No upcoming reservations for this unit.';
        this.bookingsEl.appendChild(li);
        return;
      }

      upcoming.forEach((item) => {
        const li = document.createElement('li');
        li.className = 'maison-channel-admin__booking-item';

        const guestBits = [];
        if (item.customerName)
          guestBits.push(`<span class="maison-channel-admin__guest-name">${escapeHtml(item.customerName)}</span>`);
        if (item.customerPhone) {
          guestBits.push(
            `<a class="maison-channel-admin__guest-link" href="tel:${escapeHtml(item.customerPhone)}">${escapeHtml(item.customerPhone)}</a>`
          );
        }
        if (item.customerEmail) {
          guestBits.push(
            `<a class="maison-channel-admin__guest-link" href="mailto:${escapeHtml(item.customerEmail)}">${escapeHtml(item.customerEmail)}</a>`
          );
        }

        li.innerHTML = `
          <div class="maison-channel-admin__booking-main">
            <span class="maison-channel-admin__tag maison-channel-admin__tag--${item.source}">${PLATFORM_LABEL[item.source] || item.source}</span>
            <strong>${escapeHtml(item.title)}</strong>
            <span class="maison-channel-admin__booking-dates">${formatDisplay(item.start)} → ${formatDisplay(item.end)}</span>
            ${guestBits.length ? `<div class="maison-channel-admin__guest">${guestBits.join('')}</div>` : '<div class="maison-channel-admin__guest maison-channel-admin__guest--empty">No guest details</div>'}
          </div>
        `;
        if (item.canRelease) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'maison-channel-admin__btn maison-channel-admin__btn--release';
          btn.textContent = 'Release dates';
          btn.addEventListener('click', () => this.releaseBooking(item));
          li.appendChild(btn);
        }
        this.bookingsEl.appendChild(li);
      });
    }

    async releaseBooking(item) {
      const handle = item.handle || this.activeHandle;
      const guest = item.customerName ? `\nGuest: ${item.customerName}` : '';
      const ok = await this.openModal({
        title: `Release ${item.title || item.orderName}?`,
        body: `${formatDisplay(item.start)} → ${formatDisplay(item.end)}${guest}\n\nThose nights become available again on the website.\nThe Shopify order is not cancelled — cancel it in Shopify Admin if needed.`,
        confirmLabel: 'Release dates',
        danger: true
      });
      if (!ok) return;
      this.busy = true;
      this.setStatus('Releasing stay…');
      try {
        const data = await this.apiFetch('/admin/release-booking', {
          method: 'POST',
          body: JSON.stringify({
            handle,
            orderName: item.orderName || item.title,
            checkIn: item.start,
            checkOut: item.end
          })
        });
        const unit = this.units.find((u) => u.handle === handle);
        if (unit) {
          unit.nights = data.nights || [];
          unit.rules = data.rules || '';
          unit.bookings = (unit.bookings || []).filter(
            (b) =>
              !(b.orderName === (item.orderName || item.title) && b.checkIn === item.start && b.checkOut === item.end)
          );
        }
        this.orders = this.orders.map((o) => {
          if (o.orderName === (item.orderName || item.title) && o.checkIn === item.start && o.checkOut === item.end) {
            return { ...o, datesReleased: true, canRelease: false };
          }
          return o;
        });
        this.setStatus(`Released ${item.title || item.orderName}`);
        this.renderCalendar();
        this.renderBookings();
        this.renderOrders();
        this.renderOverview();
      } catch (err) {
        this.setStatus(err.message);
      } finally {
        this.busy = false;
      }
    }

    clearSelection(rerender = true) {
      this.selectStart = null;
      this.selectEnd = null;
      this.hoverEnd = null;
      this.updateActionBar();
      if (rerender) this.paintSelection();
    }

    updateActionBar() {
      const hasStart = Boolean(this.selectStart);
      const hasEnd = Boolean(this.selectEnd);
      if (this.actionBar) this.actionBar.hidden = !hasStart;
      if (this.selStartEl) this.selStartEl.textContent = hasStart ? formatDisplay(this.selectStart) : '—';
      if (this.selEndEl) this.selEndEl.textContent = hasEnd ? formatDisplay(this.selectEnd) : 'Choose end';
      if (this.selNightsEl) {
        this.selNightsEl.textContent =
          hasStart && hasEnd ? `${nightsInclusive(this.selectStart, this.selectEnd)} nights` : '';
      }
    }

    rangeBounds() {
      if (!this.selectStart) return null;
      const end = this.selectEnd || this.hoverEnd || this.selectStart;
      return {
        start: this.selectStart <= end ? this.selectStart : end,
        end: this.selectStart <= end ? end : this.selectStart
      };
    }

    paintSelection() {
      if (!this.gridEl) return;
      const bounds = this.rangeBounds();
      this.gridEl.querySelectorAll('.maison-channel-admin__day[data-ymd]').forEach((btn) => {
        btn.classList.remove('is-in-range', 'is-range-start', 'is-range-end', 'is-selecting');
        if (!bounds) return;
        const ymd = btn.dataset.ymd;
        if (ymd >= bounds.start && ymd <= bounds.end) btn.classList.add('is-in-range');
        if (ymd === bounds.start) btn.classList.add('is-range-start', 'is-selecting');
        if (ymd === bounds.end) btn.classList.add('is-range-end');
      });
      this.updateActionBar();
    }

    renderCalendar() {
      if (!this.gridEl) return;
      const meta = this.dayMetaMap();
      this.gridEl.innerHTML = '';
      for (let offset = 0; offset < 2; offset++) {
        const monthDate = new Date(this.view.getFullYear(), this.view.getMonth() + offset, 1);
        this.gridEl.appendChild(this.buildMonth(monthDate, meta));
      }
      this.paintSelection();
    }

    buildMonth(monthDate, meta) {
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      const wrap = document.createElement('div');
      wrap.className = 'maison-channel-admin__month-block';

      const title = document.createElement('h3');
      title.className = 'maison-channel-admin__month-title';
      title.textContent = monthLabel(year, month);
      wrap.appendChild(title);

      const weekdays = document.createElement('div');
      weekdays.className = 'maison-channel-admin__weekdays';
      WEEKDAYS.forEach((d) => {
        const span = document.createElement('span');
        span.textContent = d;
        weekdays.appendChild(span);
      });
      wrap.appendChild(weekdays);

      const days = document.createElement('div');
      days.className = 'maison-channel-admin__days';
      const first = new Date(year, month, 1);
      const startPad = (first.getDay() + 6) % 7;
      for (let i = 0; i < startPad; i++) {
        const empty = document.createElement('span');
        empty.className = 'maison-channel-admin__day is-outside';
        days.appendChild(empty);
      }

      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const todayYmd = toYMD(new Date());
      for (let day = 1; day <= daysInMonth; day++) {
        const ymd = toYMD(new Date(year, month, day));
        const isPast = ymd < todayYmd;
        const info = isPast ? null : meta.get(ymd);
        const source = isPast ? 'past' : info?.source || 'open';
        const locked = Boolean(info?.locked) || isPast;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `maison-channel-admin__day is-${source}`;
        if (locked) btn.classList.add('is-locked');
        if (isPast) btn.classList.add('is-past');
        btn.textContent = String(day);
        btn.dataset.ymd = ymd;
        btn.title = isPast
          ? 'Past date'
          : source === 'open'
            ? 'Available'
            : source === 'blocked'
              ? 'Blocked (manual) — select to unblock'
              : info?.label || source;

        if (locked) btn.disabled = true;

        btn.addEventListener('mouseenter', () => {
          if (this.selectStart && !this.selectEnd && !locked && !isPast) {
            this.hoverEnd = ymd;
            this.paintSelection();
          }
        });
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.onDayClick(ymd, locked);
        });
        days.appendChild(btn);
      }

      wrap.appendChild(days);
      return wrap;
    }

    onDayClick(ymd, locked) {
      if (locked || this.busy) return;
      const todayYmd = toYMD(new Date());
      if (ymd < todayYmd) return;

      if (!this.selectStart || this.selectEnd) {
        this.selectStart = ymd;
        this.selectEnd = null;
        this.hoverEnd = null;
      } else {
        const a = this.selectStart;
        const b = ymd;
        this.selectStart = a <= b ? a : b;
        this.selectEnd = a <= b ? b : a;
        this.hoverEnd = null;
      }
      this.paintSelection();
    }

    async applySelection(mode) {
      if (!this.selectStart || this.busy) return;
      const start = this.selectStart;
      const end = this.selectEnd || this.selectStart;
      const meta = this.dayMetaMap();
      const count = nightsInclusive(start, end);

      if (mode === 'block' && count > 60) {
        const ok = await this.openModal({
          title: `Block ${count} nights?`,
          body: `${formatDisplay(start)} → ${formatDisplay(end)}\n\nThis is a large range. Continue only if you meant to block all of these nights.`,
          confirmLabel: 'Block dates',
          danger: false
        });
        if (!ok) return;
      }

      let cursor = parseYMD(start);
      const last = parseYMD(end);
      while (cursor && last && cursor <= last) {
        const info = meta.get(toYMD(cursor));
        if (info?.locked) {
          this.setStatus('Selection includes a reserved night. Pick open or manually blocked nights only.');
          return;
        }
        cursor = addDays(cursor, 1);
      }

      this.busy = true;
      this.setStatus(mode === 'block' ? 'Blocking…' : 'Unblocking…');
      try {
        const data = await this.apiFetch(mode === 'block' ? '/admin/block' : '/admin/unblock', {
          method: 'POST',
          body: JSON.stringify({ handle: this.activeHandle, start, end })
        });
        const unit = this.activeUnit();
        if (unit) {
          unit.nights = data.nights || [];
          unit.rules = data.rules || '';
        }
        this.setStatus(
          mode === 'block'
            ? `Blocked ${formatDisplay(start)}${start !== end ? ' → ' + formatDisplay(end) : ''}`
            : `Unblocked ${formatDisplay(start)}${start !== end ? ' → ' + formatDisplay(end) : ''}`
        );
        this.selectStart = null;
        this.selectEnd = null;
        this.hoverEnd = null;
        this.renderCalendar();
        this.renderOverview();
      } catch (err) {
        this.setStatus(err.message);
      } finally {
        this.busy = false;
      }
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-maison-channel-admin]').forEach((el) => {
      if (el.dataset.ready === 'true') return;
      el.dataset.ready = 'true';
      new ChannelAdmin(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
